<script setup lang="ts">
/*
* Компонент UniversalStatsTable - Универсальная таблица статистики
*
* Основные функции:
* - Отображение агрегированных данных по нежелательным явлениям (adverse) и тикетам (ticket)
* - Серверная пагинация, фильтрация и сортировка данных
* - Интеграция с древовидным фильтром UniversalFilterTree
* - Управление активными фильтрами через систему "чипов"
* - Выбор временных интервалов через DatePicker
*/

import { computed, ref, watch } from 'vue'
import { VIconBtn } from 'vuetify/labs/VIconBtn'
import { useDisplay, useTheme } from 'vuetify/framework'

import { useUniversalStatsStore } from '@/modules/universalStats/stores/universalStats'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { formatDateOnly, toApiDate } from '@/utils/formatters'
import { useTableItemsPerPage } from '@/composables/useTableItemsPerPage'

// Импорт нового компонента дерева
import UniversalFilterTree from '@/components/createdUi/UniversalFilterTree.vue'

import type { IAdverseStatsRequest } from '@/modules/universalStats/types/IAdverseStatsRequest'
import type { ITicketStatsRequest } from '@/modules/universalStats/types/ITicketStatsRequest'

// Входные параметры компонента
const props = defineProps<{
  // Тип статистики: нежелательные явления или тикеты
  statsType: 'adverse' | 'ticket'
  // Опциональный проп для передачи кастомного стора
  customStore?: any
}>()

// Инициализация темы оформления
const theme = useTheme()

// Определение размера экрана для адаптивности (xs - мобильные устройства)
const { xs } = useDisplay()

// Store для управления глобальными состояниями обратной связи (загрузка, уведомления)
const feedbackStore = useFeedbackStore()

// Основной Store для управления данными статистики
const statsStore = useUniversalStatsStore()
const { itemsPerPage } = useTableItemsPerPage('universalStats', 25)
statsStore.itemsPerPage = itemsPerPage.value
watch(itemsPerPage, newVal => {
  statsStore.itemsPerPage = newVal
})

// Управление состоянием открытия меню (Popover) для каждого фильтра в заголовках
const filterMenus = ref<Record<string, boolean>>({})

// Вычисляемый список вкладок (агрегаций) в зависимости от типа статистики
const currentTabs = computed(() => statsStore.aggregateItems[props.statsType])

// Индекс текущей активной вкладки
const formTabValue = ref<number>(0)

// Номер текущей страницы пагинации
const currentStatsPage = ref(1)

// Состояние процесса догрузки данных (Infinite loading / Server side pagination)
const isLoadingMore = ref(false)

// Локальный буфер для хранения выбранных ID до нажатия кнопки "Применить"
const tempSelectedFilters = ref<Record<string, string[]>>({})

// Дата начала периода (фильтр по дате)
const dateFrom = ref<string | null>(null)

// Дата окончания периода (фильтр по дате)
const dateTo = ref<string | null>(null)

// Флаги управления видимостью календарей
const dateFromMenu = ref(false)
const dateToMenu = ref(false)

const allLeafHeaders = computed(() => {
  const getLeaves = (headers: any[]): any[] => {
    return headers.reduce((acc, header) => {
      if (header.key) acc.push(header)
      if (header.children) acc.push(...getLeaves(header.children))
      return acc
    }, [])
  }
  return getLeaves(statsStore.currentTableHeaders)
})

// Вычисляемый массив элементов для отображения на текущей странице (слайс из общего массива стора)
const displayedStatsItems = computed(() => {
  const start = (currentStatsPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return statsStore.statsItems.slice(start, end)
})

/**
 * Инициализация запроса статистики при смене вкладки агрегации
 * @param value Объект запроса с параметрами агрегации
 */
const statsRequest = async (value: IAdverseStatsRequest | ITicketStatsRequest) => {
  currentStatsPage.value = 1
  statsStore.currentAggregateType = value
  statsStore.nextCursor = null
  statsStore.statsItems = []
  statsStore.activeFilters = {}
  filterMenus.value = {} // Закрываем все меню при смене таба
  await statsStore.fetchStats(props.statsType)
}

// Флаг скрытия кнопки "Вперед" в пагинации, если данных больше нет
const hideNextButton = computed(() => {
  return !statsStore.nextCursor
    && currentStatsPage.value * itemsPerPage.value >= statsStore.totalStatsItemsCount
})

/**
 * Получение иконки фильтра в зависимости от его активности
 * @param isActive Состояние активности фильтра в колонке
 */
const getFilterIcon = (isActive: boolean) => isActive ? 'ri-filter-fill' : 'ri-filter-2-line'

/**
 * Проверка, применен ли фильтр к конкретной колонке
 * @param key Ключ поля таблицы
 */
const isFilterActive = (key?: string): boolean => {
  if (!key) return false
  const values = statsStore.activeFilters[key]
  return Array.isArray(values) && values.length > 0
}

/**
 * Формирует список активных чипов, включая выбранные даты и фильтры из дерева.
 */
const activeFilterChips = computed(() => {
  const chips: Array<{ key: string; label: string; value: string; fieldTitle: string; type: 'filter' | 'date' }> = []

  // 1. РЕКУРСИВНЫЙ ПОИСК (Ищет элемент по ID на любой глубине)
  const findLabelRecursive = (nodes: any[], id: any): string | undefined => {
    if (!nodes || !Array.isArray(nodes)) return undefined

    for (const node of nodes) {
      if (String(node.id) === String(id)) return node.label

      if (node.children && node.children.length > 0) {
        const found = findLabelRecursive(node.children, id)
        if (found) return found
      }
    }
    return undefined
  }

  // 2. Добавляем чип для "Дата с"
  if (statsStore.dateAfter) {
    chips.push({
      key: 'created_after',
      label: formatDateOnly(statsStore.dateAfter), // формат дд.мм.гггг
      value: statsStore.dateAfter,
      fieldTitle: 'Дата с',
      type: 'date',
    })
  }

  // 2. Добавляем чип для "Дата по", если она выбрана
  if (statsStore.dateBefore) {
    chips.push({
      key: 'created_before',
      label: formatDateOnly(statsStore.dateBefore),
      value: statsStore.dateBefore,
      fieldTitle: 'Дата по',
      type: 'date',
    })
  }

  // 4. Логика для фильтров из дерева (с рекурсией)
  Object.keys(statsStore.activeFilters).forEach(key => {
    const selectedIds = statsStore.activeFilters[key]
    if (!Array.isArray(selectedIds) || selectedIds.length === 0) return

    const header = statsStore.currentTableHeaders.find(h => h.key === key)
    const fieldTitle = header ? header.title : key

    selectedIds.forEach(id => {
      // Ищем сначала в кеше поиска, потом в основном фильтре
      let label = findLabelRecursive(statsStore.searchChoices[key], id)

      if (!label) {
        label = findLabelRecursive(statsStore.filterChoices[key], id)
      }

      // Если не нашли — показываем ID как строку
      label = label || String(id)

      chips.push({
        key,
        label,
        value: id,
        fieldTitle,
        type: 'filter',
      })
    })
  })

  return chips
})

/**
 * Удаляет значение фильтра (или даты) и обновляет таблицу
 */
const removeFilterValue = async (chip: { key: string; value: string; type?: string }) => {
  if (chip.type === 'date') {
    // Если это чип даты — используем наши функции очистки
    if (chip.key === 'created_after') {
      dateFrom.value = null
      statsStore.dateAfter = null
    } else if (chip.key === 'created_before') {
      dateTo.value = null
      statsStore.dateBefore = null
    }
  } else {
    // Если это обычный фильтр из дерева
    statsStore.activeFilters[chip.key] = statsStore.activeFilters[chip.key].filter(id => id !== chip.value)
  }

  // Сбрасываем пагинацию и обновляем данные
  currentStatsPage.value = 1
  statsStore.nextCursor = null
  statsStore.statsItems = []
  await statsStore.fetchStats(props.statsType)
}

/**
 * Очистка начальной даты
 */
const clearDateFrom = async () => {
  dateFrom.value = null
  statsStore.dateAfter = null

  currentStatsPage.value = 1
  statsStore.statsItems = []
  await statsStore.fetchStats(props.statsType)
}

/**
 * Очистка конечной даты
 */
const clearDateTo = async () => {
  dateTo.value = null
  statsStore.dateBefore = null

  currentStatsPage.value = 1
  statsStore.statsItems = []
  await statsStore.fetchStats(props.statsType)
}

/**
 * Полный сброс всех примененных фильтров (включая даты)
 */
const resetAllFilters = async () => {
  statsStore.activeFilters = {}
  tempSelectedFilters.value = {}
  // Очистка дат в сторе и локально
  statsStore.dateAfter = null
  statsStore.dateBefore = null
  dateFrom.value = null
  dateTo.value = null

  currentStatsPage.value = 1
  statsStore.nextCursor = null
  statsStore.statsItems = []
  await statsStore.fetchStats(props.statsType)
}

/**
 * Обработка клика на иконку фильтра: загрузка вариантов и открытие меню
 */
const handleFilterClick = async (header: any) => {
  if (!header.key) return

  // Инициализируем локальный буфер текущими значениями из стора (чтобы галочки не пропадали при открытии)
  tempSelectedFilters.value[header.key] = [...(statsStore.activeFilters[header.key] || [])]

  // Сначала загружаем данные с бека для отображения в дереве
  await statsStore.fetchFilterChoices(
    header.key,
    '',
    undefined,
    props.statsType,
  )

  // Открываем меню именно для этой колонки
  filterMenus.value[header.key] = true
}

/**
 * Вызывается при нажатии "Применить" в компоненте дерева фильтрации
 */
const applyFilter = async (key: string) => {
  if (!key) return

  // Переносим значения из локального буфера в основной стор (что бы появились чипы после активации фильтрации а не сразу)
  statsStore.activeFilters[key] = [...(tempSelectedFilters.value[key] || [])]

  filterMenus.value[key] = false // Закрываем меню
  currentStatsPage.value = 1
  statsStore.nextCursor = null
  statsStore.statsItems = []

  // Запрашиваем обновленную статистику с учетом выбранных фильтров
  await statsStore.fetchStats(props.statsType)
}

/**
 * Универсальный обработчик изменений опций таблицы (страница, сортировка, лимит)
 * @param options Объект настроек VDataTableServer
 */
function handleOptionsChange(options: { page?: number; itemsPerPage?: number; sortBy?: { key: string; order: string }[] }) {
  const page = Number(options.page || 1)
  const perPage = Number(options.itemsPerPage || 25)
  let sortParam = ''

  // Формирование строки сортировки для API (например, "-name" для DESC)
  if (options.sortBy && options.sortBy.length > 0) {
    const { key, order } = options.sortBy[0]
    sortParam = order === 'desc' ? `-${key}` : key
  }

  // Если изменилась сортировка или количество строк — сбрасываем данные и грузим заново
  if (sortParam !== statsStore.currentSortString || perPage !== itemsPerPage.value) {
    statsStore.currentSortString = sortParam
    itemsPerPage.value = perPage
    currentStatsPage.value = 1
    statsStore.fetchStats(props.statsType, undefined, perPage, sortParam)
    return
  }

  currentStatsPage.value = page
  const itemsNeeded = page * perPage
  const currentLoaded = statsStore.statsItems.length

  // Если нужны данные для следующей страницы и есть курсор — запрашиваем следующую порцию
  if (!isLoadingMore.value && itemsNeeded > currentLoaded && statsStore.nextCursor) {
    isLoadingMore.value = true
    let cursorParam: string | null
    try {
      cursorParam = new URL(statsStore.nextCursor).searchParams.get('cursor')
    } catch {
      cursorParam = statsStore.nextCursor
    }
    statsStore.fetchStats(props.statsType, cursorParam ?? undefined, perPage, sortParam)
      .finally(() => {
        isLoadingMore.value = false
      })
  }
}

/**
 * Обработчик выбора начальной даты
 */
async function onDateFromSelect(date: string | null) {
  if (date) {
    // 1. Форматируем для отображения в инпуте (дд.мм.гггг)
    dateFrom.value = formatDateOnly(date)
    // 2. Форматируем для отправки в API (гггг-мм-дд)
    statsStore.dateAfter = toApiDate(date)
  } else {
    statsStore.dateAfter = null
  }
  dateFromMenu.value = false

  currentStatsPage.value = 1
  statsStore.statsItems = []
  await statsStore.fetchStats(props.statsType)
}

/**
 * Обработчик выбора конечной даты
 */
async function onDateToSelect(date: string | null) {
  if (date) {
    // 1. Форматируем для отображения в инпуте (дд.мм.гггг)
    dateTo.value = formatDateOnly(date)
    // 2. Форматируем для отправки в API (гггг-мм-дд)
    statsStore.dateBefore = toApiDate(date)
  } else {
    statsStore.dateBefore = null
  }
  dateToMenu.value = false

  currentStatsPage.value = 1
  statsStore.statsItems = []
  await statsStore.fetchStats(props.statsType)
}

// Отслеживаем изменение типа статистики для перезагрузки данных и сброса стора
watch(() => props.statsType, async newType => {
  formTabValue.value = 0
  currentStatsPage.value = 1
  isLoadingMore.value = false
  dateFrom.value = null
  dateTo.value = null

  statsStore.$reset()
  statsStore.itemsPerPage = itemsPerPage.value
  statsStore.currentAggregateType = 'coordinator'

  await statsStore.fetchStats(newType)
}, { immediate: true })
</script>

<template>
  <div class="d-flex flex-column gap-2">
    <header :class="{ 'd-flex flex-column gap-2 mb-4': xs } ">
      <div :class="xs ? 'd-flex flex-column gap-2 w-100' : 'd-flex align-center justify-space-between w-100'">
        <VTabs v-model="formTabValue">
          <div class="d-flex gap-2">
            <VTab
              v-for="(item, index) in currentTabs"
              :key="item.value"
              :value="index"
              @click="statsRequest(item.value as IAdverseStatsRequest | ITicketStatsRequest)"
            >
              {{ item.name }}
            </VTab>
          </div>
        </VTabs>

        <div class="d-flex align-center gap-2" :class="{ 'w-100': xs }">
          <!--
            <VIconBtn
            color="secondary"
            class="rounded-sm"
            icon="ri-share-2-line"
            :icon-size="20"
            variant="outlined"
            :rounded="false"
            :size="34"
            />
          -->
        </div>
      </div>
    </header>

    <div class="wrapper">
      <div class="d-flex flex-column">
        <div class="d-flex justify-space-between align-center gap-4 pa-4" :class="{ 'flex-column': xs }">
          <div class="d-flex align-center gap-2" :class="{ 'flex-column w-100': xs }">
            <VMenu
              v-model="dateFromMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              location="bottom start"
              min-width="auto"
            >
              <template #activator="{ props }">
                <VTextField
                  v-bind="props"
                  v-model="dateFrom"
                  variant="outlined"
                  label="Дата с"
                  density="compact"
                  append-inner-icon="ri-calendar-line"
                  hide-details
                  readonly
                  min-width="180"
                  clearable
                  class="invisible-readonly"
                  :class="{ 'w-100': xs }"
                  @click="dateFromMenu = true"
                  @click:append-inner.prevent.stop="dateFromMenu = true"
                  @click:clear="clearDateFrom"
                />
              </template>
              <VDatePicker :model-value="dateFrom" @update:model-value="onDateFromSelect" />
            </VMenu>

            <VMenu
              v-model="dateToMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              location="bottom start"
              min-width="auto"
            >
              <template #activator="{ props }">
                <VTextField
                  v-bind="props"
                  v-model="dateTo"
                  variant="outlined"
                  label="Дата по"
                  density="compact"
                  append-inner-icon="ri-calendar-line"
                  hide-details
                  readonly
                  min-width="180"
                  clearable
                  class="invisible-readonly"
                  :class="{ 'w-100': xs }"
                  @click="dateToMenu = true"
                  @click:append-inner.prevent.stop="dateToMenu = true"
                  @click:clear="clearDateTo"
                />
              </template>
              <VDatePicker :model-value="dateTo" @update:model-value="onDateToSelect" />
            </VMenu>
          </div>

          <VBtn
            v-if="Object.keys(statsStore.activeFilters).some(k => statsStore.activeFilters[k].length > 0) || statsStore.dateAfter || statsStore.dateBefore"
            :class="{ 'w-100': xs }"
            prepend-icon="ri-delete-bin-line"
            variant="outlined"
            color="secondary"
            class="text-color-secondary"
            :loading="feedbackStore.isGlobalLoading"
            min-height="36"
            @click="resetAllFilters"
          >
            Сбросить фильтры
          </VBtn>
        </div>

        <div v-if="activeFilterChips.length" class="filter-chip d-flex flex-wrap gap-2 px-4 pb-4">
          <VChip
            v-for="chip in activeFilterChips"
            :key="`${chip.key}-${chip.value}`"
            closable
            @click:close="removeFilterValue(chip)"
          >
            <span class="font-weight-bold mr-1">{{ chip.fieldTitle }}:</span>
            {{ chip.label }}
          </VChip>
        </div>
      </div>

      <VDataTableServer
        :items="displayedStatsItems"
        :items-length="statsStore.totalStatsItemsCount"
        :headers="statsStore.currentTableHeaders"
        :items-per-page="itemsPerPage"
        class="no-row-gaps cursor-pointer-table custom-data-table custom-data-table"
        item-value="id"
        :class="[{ 'dark-theme': theme.global.current.value.dark }]"
        @update:options="handleOptionsChange"
      >
        <template
          v-for="header in allLeafHeaders"
          :key="header.key"
          #[`header.${header.key}`]="{ column }"
        >
          <div v-if="header.key" class="d-flex align-center">
            <div class="d-flex align-center">
              <span>{{ column.title }}</span>
              <VIcon
                v-if="column.sortable"
                size="16"
                class="ml-1 custom-sort-icon"
                :icon="statsStore.currentSortString?.replace('-', '') === header.key
                  ? 'ri-arrow-up-fill'
                  : 'ri-arrow-up-line'"
                :class="{
                  'is-active': statsStore.currentSortString?.replace('-', '') === header.key,
                  'is-desc': statsStore.currentSortString === `-${header.key}`,
                }"
              />
            </div>
            <VMenu
              v-if="header.filterable"
              v-model="filterMenus[header.key]"
              :close-on-content-click="false"
              location="bottom start"
              transition="scale-transition"
            >
              <template #activator="{ props: menuProps }">
                <VIconBtn
                  v-bind="menuProps"
                  variant="plain"
                  size="small"
                  class="filter-icon-btn"
                  :icon="getFilterIcon(isFilterActive(header.key))"
                  @click.stop="handleFilterClick(header)"
                />
              </template>

              <UniversalFilterTree
                :field-key="header.key as string"
                :store="statsStore"
                :model-value="tempSelectedFilters[header.key as string] || []"
                :placeholder="`Поиск: ${column.title}`"
                @update:model-value="tempSelectedFilters[header.key as string] = $event"
                @apply="applyFilter(header.key as string)"
              />
            </VMenu>
          </div>
        </template>

        <template #bottom>
          <div class="custom-data-table-footer" :class="{ 'hide-next-button': hideNextButton }">
            <VDataTableFooter
              :page="currentStatsPage"
              :items-per-page="itemsPerPage"
              :items-length="statsStore.totalStatsItemsCount"
              :show-current-page="true"
              :items-per-page-options="[25, 50, 100]"
              @update:items-per-page="handleOptionsChange({ itemsPerPage: $event })"
              @update:page="handleOptionsChange({ page: $event })"
            />
          </div>
        </template>
      </VDataTableServer>
    </div>
  </div>
  <RouterView />
</template>

<style scoped lang="scss">
/* --- СТИЛИ ЗАГОЛОВКОВ ТАБЛИЦЫ (как в НС / Заявках) --- */
:deep(.custom-data-table .v-data-table__th) {
  background-color: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity)) !important;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity)) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  line-height: 22px !important;
  position: relative;
  padding-inline: 1rem;
}

:deep(.custom-data-table .v-data-table__td) {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  padding-inline: 1rem;
}

:deep(.v-data-table__td) {
  padding-block: 0.75rem !important;
}

/* Фикс для липкой шапки в многоуровневых таблицах: смещаем второй ряд заголовков вниз на высоту первого */
:deep(.custom-data-table thead tr:nth-child(2) .v-data-table__th) {
  inset-block-start: 56px !important;
}

/* Вертикальные черточки-разделители между заголовками */
:deep(.custom-data-table .v-data-table__th:not(:last-child))::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  width: 2px;
  height: 14px;
  background-color: rgb(var(--v-theme-on-background), var(--v-custom-opacity));
}

/* --- СТИЛИ ФУТЕРА (ПАГИНАЦИИ) --- */

/* Центрирование всей полосы футера */
:deep(.v-data-table-footer) {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.custom-data-table-footer {
  width: 100%;

  :deep(.v-data-table-footer) {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;

    /* Скрываем текст "Строк на странице:" */
    .v-data-table-footer__items-per-page > span:first-child {
      display: none !important;
    }

    /* Меняем порядок элементов: Кнопки пагинации в начало */
    .v-data-table-footer__pagination {
      order: 1;
    }

    /* Выбор кол-ва строк в середину */
    .v-data-table-footer__items-per-page {
      order: 2;
    }

    /* Инфо-текст (например, 1-25 из 100) в конец */
    .v-data-table-footer__info {
      order: 3;
    }

    /* Всегда скрываем кнопку "Перейти на последнюю страницу" (несовместимо с курсором) */
    .v-pagination__last {
      display: none !important;
    }
  }

  /* Скрываем кнопку "Вперед", если в сторе нет ссылки на следующий курсор (конец данных) */
  &.hide-next-button {
    :deep(.v-pagination__next) {
      display: none !important;
    }
  }
}

:deep(
  .custom-data-table thead tr:first-child th[rowspan="1"][colspan]:not([colspan="1"])
  .v-data-table-header__content
) {
  justify-content: center;
  text-align: center;
}

:deep(.v-data-table-header__sort-icon) {
  display: none !important;
}

/* 1. Скрываем системные иконки Vuetify, чтобы они не мешали нашим кастомным */
:deep(.v-data-table-header__sort-icon) {
  display: none !important;
}

/* 2. Базовые стили для нашей кастомной иконки сортировки */
.custom-sort-icon {
  transition: transform 0.3s ease, opacity 0.2s ease, color 0.2s ease;
  opacity: 0.15; /* Почти прозрачная по умолчанию */
  display: inline-block;
  vertical-align: middle;
  cursor: pointer;

  /* Состояние АКТИВНОЙ сортировки (когда поле в сторе совпадает с колонкой) */
  &.is-active {
    opacity: 1 !important;
    color: rgb(var(--v-theme-primary)) !important;
  }

  /* Переворот иконки на 180 градусов при DESC (минус в строке сортировки) */
  &.is-desc {
    transform: rotate(180deg);
  }
}

/* Делаем иконку чуть виднее при наведении на ячейку заголовка */
:deep(.v-data-table__th--sortable:hover) .custom-sort-icon {
  opacity: 0.6;
}
</style>
